import isNil from 'lodash/isNil';

import {getAllFlags} from 'ld-client';

export type LogSource =
    | 'app-routes'
    | 'aws-web-rtc-video'
    | 'center-sign-in-modal'
    | 'direct-stream-video'
    | 'direct-stream-video-rtc'
    | 'exams-comet-websocket-manager'
    | 'exams-control-interface'
    | 'exams-student-interface'
    | 'html-video-one'
    | 'kinesis-client'
    | 'live-session-viewer'
    | 'lms-integration-routes'
    | 'lms-link-course'
    | 'lms-link-course-confirm-existing'
    | 'lms-link-course-confirm-new'
    | 'lms-link-course-success'
    | 'lms-module'
    | 'main'
    | 'multi-room-chat-manager'
    | 'sfu-peer-manager'
    | 'sfu-socket-api'
    | 'socket-cluster-wrapper'
    | 'vera-video'
    | 'viewer-gudrid-wrapper'
    | 'viewer-gudrid-popout'
    | 'web-rtc-paging'
    | 'web-rtc-video';

type LogSourcesConfig = {
    exclude: LogSource[];
    include: LogSource[];
};

const DEFAULT_CONFIG: LogSourcesConfig = {
    exclude: [
        'app-routes',
        'aws-web-rtc-video',
        'exams-student-interface',
        'kinesis-client',
        'multi-room-chat-manager',
        'vera-video',
    ],
    include: [
        'center-sign-in-modal',
        'direct-stream-video',
        'direct-stream-video-rtc',
        'exams-control-interface',
        'lms-integration-routes',
        'lms-link-course',
        'lms-link-course-confirm-existing',
        'lms-link-course-confirm-new',
        'lms-link-course-success',
        'lms-module',
        'main',
        'sfu-peer-manager',
        'sfu-socket-api',
        'socket-cluster-wrapper',
        'viewer-gudrid-wrapper',
        'web-rtc-paging',
        'web-rtc-video',
        'viewer-gudrid-popout',
    ],
};

/**
 * General purpose logger for the UI that can send output to the browser console. Each instance is associated with a
 * "source", such as a file or module, to assist with readability, searching, and filtering. You can use the Logger
 * instance much like the native `console`, via the log level methods `debug`, `error`, `info`, and `warn`. If the
 * source of the log message matches one of the enabled sources, the log message will be logged.
 *
 * Logging sources are configurable via the LaunchDarkly flag `enabled-ui-log-sources`.
 */
export default class Logger {
    source: LogSource;

    constructor(source: LogSource) {
        if (isNil(source)) {
            throw new Error('Missing one or more required parameters');
        }

        this.source = source;
    }

    // checks at runtime whether this source is permitted to log
    enabled() {
        const flags = getAllFlags();
        const configFromFlag = flags['enabled-ui-log-sources'];
        const config = isNil(configFromFlag) ? DEFAULT_CONFIG : configFromFlag;

        return config.include.includes(this.source) && !config.exclude.includes(this.source);
    }

    prefix() {
        return `${new Date().toISOString()} ${this.source}:`;
    }

    // The following functions wrap the browser's `console` so we can decide at
    // runtime whether a given source is permitted to log or not, as well as
    // avoid eslint warnings for logging.

    debug(...args) {
        if (!this.enabled()) {
            return;
        }

        // some browsers lack console.debug
        if (isNil(console.debug)) {
            // eslint-disable-next-line no-console
            console.info(this.prefix(), ...args);
        } else {
            console.debug(this.prefix(), ...args);
        }
    }

    // logging to backend is async but we don't need to await it here
    error(...args) {
        if (!this.enabled()) {
            return;
        }

        console.error(this.prefix(), ...args);
    }

    info(...args) {
        if (!this.enabled()) {
            return;
        }

        // eslint-disable-next-line no-console
        console.info(this.prefix(), ...args);
    }

    warn(...args) {
        if (!this.enabled()) {
            return;
        }

        // eslint-disable-next-line no-console
        console.warn(this.prefix(), ...args);
    }
}
